import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';

import Dialog from '~components/Dialog/Dialog';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { RowItemLabelValue } from '~components/Job/RowItemLabelValue';
import { EmailIntakeMessage } from '~hooks/useEmailIntakeMessages/models';
import useOrderEmailIntakeMessageQuery from '~queries/useOrderEmailIntakeMessageQuery';

interface OriginalEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
}

interface OriginalEmailProps {
  emailIntakeMessage: EmailIntakeMessage;
}

interface OriginalEmailDialogContentProps {
  orderId: string;
}

function OriginalEmail({ emailIntakeMessage }: OriginalEmailProps) {
  const sender = emailIntakeMessage.fromName
    ? t('dispatch.order.original_email.sender', {
        email: emailIntakeMessage.fromEmail,
        interpolation: { escapeValue: false },
        name: emailIntakeMessage.fromName,
      })
    : emailIntakeMessage.fromEmail;

  return (
    <>
      <Table>
        <TableBody>
          <RowItemLabelValue label={t('common.sender')} value={sender} />
          <RowItemLabelValue
            label={t('dispatch.order.original_email.received')}
            value={dayjs.tz(emailIntakeMessage.createdAt).format('MMM D, YYYY h:mm A')}
          />
          <RowItemLabelValue
            label={t('email_documents.subject_label')}
            value={emailIntakeMessage.subject}
          />
        </TableBody>
      </Table>

      <Typography sx={{ mt: 2, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
        {emailIntakeMessage.body}
      </Typography>
    </>
  );
}

function OriginalEmailDialogContent({ orderId }: OriginalEmailDialogContentProps) {
  const { emailIntakeMessage, isError } = useOrderEmailIntakeMessageQuery(orderId);

  if (emailIntakeMessage) {
    return <OriginalEmail emailIntakeMessage={emailIntakeMessage} />;
  }

  if (isError) {
    return (
      <Typography variant="body2">
        {t('error_messages.email_intake_messages.failed_to_fetch')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress aria-label={t('common.loading')} />
    </Box>
  );
}

export default function OriginalEmailDialog({
  isOpen,
  onClose,
  orderId,
}: OriginalEmailDialogProps) {
  return (
    <Dialog isOpen={isOpen} maxWidth="md" onClose={onClose}>
      <DialogHeader onClose={onClose}>
        {t('dispatch.order.original_email.title')}
      </DialogHeader>

      <DialogContent>
        <OriginalEmailDialogContent orderId={orderId} />
      </DialogContent>
    </Dialog>
  );
}
